import { colors, typography } from "design"
import {
  Layout,
  PageTitle,
  Notification,
  useNotificationContext,
  NotificationContext,
} from "widgets"
import { useForm, FormProvider } from "react-hook-form"
import {
  useCallback,
  useMemo,
  useRef,
  createContext,
  useEffect,
  cloneElement,
  memo,
} from "react"
import { classes, belowViewPort } from "utils"
import { ErrorBoundary } from "react-error-boundary"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

const groupStyle = {
  flexBasis: 600,
  gap: 18,
  flexGrow: 1,
  flexWrap: "wrap",
  alignItems: "flex-start",
  "> div": {
    flexGrow: 1,
    flexBasis: 300,
    minWidth: 300,
    ...belowViewPort(768, { minWidth: 0 }),
  },
}

export const ExplorerContext = createContext(null)

export const FormRow = ({ className, children }) => {
  const balanced = useMemo(() => !(_.size(children) % 2), [children])

  return (
    <Layout
      className={classes(className, "form-row")}
      css={[
        {
          alignItems: "flex-start",
          flexWrap: "wrap",
          width: "100%",
          gap: 18,
          justifyContent: "space-between",
        },
        !balanced && {
          "> div": {
            flexGrow: 1,
          },
        },
      ]}
    >
      {!balanced ? (
        children
      ) : (
        <>
          <Layout css={groupStyle}>
            {children[0]}
            {children[1]}
          </Layout>
          {_.size(children) > 2 && (
            <Layout css={groupStyle}>
              {children[2]}
              {children[3]}
            </Layout>
          )}
        </>
      )}
    </Layout>
  )
}

const Explorer = memo(
  ({
    title,
    className,
    children,
    formHeading,
    formStyle,
    componentStyle,
    defaultValues,
    showFormStyle,
    onClose,
    hideRefresh,
    hideClose,
    onSubmit,
    Component,
    skipPagination = false,
  }) => {
    const methods = useForm({
      defaultValues,
      mode: "onSubmit",
      reValidateMode: "onChange",
      criteriaMode: "all",
    })

    const {
      handleSubmit,
      reset,
      watch,
      control,
      trigger,
      setValue,
      setError,
      clearErrors,
    } = methods
    const formValues = watch()
    const [searchParams, setSearchParams] = useSearchParams()
    const explorerRef = useRef(null)
    const navigate = useNavigate()
    const location = useLocation()
    const updatedParams = new URLSearchParams(searchParams)
    updatedParams.set("page", 0) // Set default value for page
    updatedParams.set("size", 10) // Set default value for limit

    const { type, message, notifySuccess, notifyError } =
      useNotificationContext({
        ref: explorerRef,
      })

    const notificationContextValue = useMemo(
      () => ({ type, message, notifySuccess, notifyError }),
      [type, message, notifySuccess, notifyError],
    )

    const navigateToBase = useCallback(() => {
      navigate("/")
    }, [navigate])

    useEffect(() => reset(defaultValues), [defaultValues, reset])

    const resetParams = useCallback(() => {
      if (skipPagination) return
      const updatedParams = new URLSearchParams(searchParams)
      // Reset only 'page' and 'limit' while keeping others untouched
      updatedParams.set("page", 0) // Set default value for page
      updatedParams.set("size", 10) // Set default value for limit

      setSearchParams(updatedParams, { state: location.state })
    }, [location.state, searchParams, setSearchParams, skipPagination])

    const onResetForm = useCallback(() => {
      reset()
      resetParams()
    }, [reset, resetParams])

    const triggerSubmit = useCallback(
      async (data) => {
        clearErrors()
        const isValid = await methods.trigger()
        if (!isValid) return
        resetParams()
        onSubmit({
          data: _.omitBy(
            data,
            (value) =>
              value === null ||
              value === undefined ||
              value === "" ||
              (_.isArray(value) && _.isEmpty(value)),
          ),
          notifySuccess,
          notifyError,
          resetForm: onResetForm,
          setError,
          clearErrors,
        })
      },
      [
        methods,
        resetParams,
        onSubmit,
        notifySuccess,
        notifyError,
        onResetForm,
        setError,
        clearErrors,
      ],
    )

    const triggerFormSubmission = useCallback(
      () => handleSubmit(triggerSubmit)(),
      [handleSubmit, triggerSubmit],
    )

    useEffect(() => {
      const updatedParams = new URLSearchParams(searchParams)
      if (
        _.isNil(updatedParams.get("page")) ||
        _.isNil(updatedParams.get("size"))
      )
        resetParams()
    }, [resetParams, searchParams])

    return (
      <ErrorBoundary
        className="explorer-error-boundary"
        fallbackRender={({ error }) => (
          <div
            role="alert"
            className="error-boundary-fallback"
            css={{ padding: 40 }}
          >
            <h1>Something went wrong:</h1>
            <pre style={{ color: "red" }}>{error.message}</pre>
          </div>
        )}
      >
        <Layout
          ref={explorerRef}
          className={classes("explorer", className)}
          css={[
            { width: "inherit" },
            Component && {
              background: colors.darkCream,
            },
          ]}
          vertical
        >
          <FormProvider {...methods}>
            <form
              className="form"
              onSubmit={handleSubmit(triggerSubmit)}
              css={{
                width: "100%",
                background: colors.darkCream,
              }}
            >
              <div
                className="top"
                css={[
                  {
                    padding: "30px 56px",
                    ...belowViewPort(768, { padding: 20 }),
                  },
                  (Component || showFormStyle) && {
                    background: colors.primaryBlueGrey,
                    label: {
                      color: colors.white,
                    },
                    ".title,.disclaimer": {
                      color: colors.white,
                    },
                    ".form-heading": {
                      color: colors.white,
                      borderLeftColor: colors.white,
                    },
                    ".form-fields button": {
                      background: "var(--resbird-button-secondary-background)",
                    },
                  },
                  formStyle,
                ]}
              >
                <Notification {...{ type, message }} />
                {title && (
                  <PageTitle
                    title={title}
                    titleStyle={typography.title}
                    css={{
                      paddingBottom: 2,
                      borderBottom: `1px solid ${colors.grey200}`,
                      color: colors.white,
                    }}
                    onRefresh={onResetForm}
                    onClose={onClose ? onClose : navigateToBase}
                    {...{ hideRefresh, hideClose }}
                  />
                )}
                {children && (
                  <Layout
                    className="explorer-form"
                    css={{ padding: "24px 0", width: "100%", gap: 26 }}
                    vertical
                  >
                    {formHeading && (
                      <div
                        className="form-heading"
                        css={typography.formHeading}
                      >
                        {formHeading}
                      </div>
                    )}
                    <Layout
                      className="form-fields"
                      css={{ gap: 18, width: "100%" }}
                      vertical
                    >
                      {children}
                    </Layout>
                  </Layout>
                )}
              </div>
              {Component && (
                <div
                  className={classes(className, "bottom")}
                  css={[
                    {
                      padding: "30px 56px",
                      width: "100%",
                      background: colors.darkCream,
                    },
                    belowViewPort(768, { padding: 20 }),
                    componentStyle,
                  ]}
                >
                  <NotificationContext.Provider
                    value={notificationContextValue}
                  >
                    {cloneElement(Component, {
                      formValues,
                      control,
                      trigger,
                      watch,
                      setValue,
                      clearErrors,
                      triggerFormSubmission,
                    })}
                  </NotificationContext.Provider>
                </div>
              )}
            </form>
          </FormProvider>
        </Layout>
      </ErrorBoundary>
    )
  },
)

Explorer.displayName = "Explorer"

export default Explorer
